/**
 * Agent Run hooks —— 长程运行的 React Query 封装（切片 2 波次 B）。
 *
 * 详情查询不设轮询间隔：实时进度由 useEventStream 的事件增量覆盖，本查询
 * 只在进入页面 / 取消后 / 降级轮询（fetchSnapshot 回调）时重拉权威快照。
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { agentRunsApi } from "../api/agentRuns";
import type {
  AgentRunCreatePayload,
  AgentRunInputPayload,
  AgentRunStatus,
} from "../types/agentRun";

export const agentRunQueryKeys = {
  list: (params: unknown) => ["agent-runs", "list", params] as const,
  detail: (id: string) => ["agent-runs", "detail", id] as const,
};

/** GET /agent-runs 列表参数（透传 agentRunsApi.list）。 */
export interface AgentRunListParams {
  status?: AgentRunStatus;
  matterId?: string;
  scheduledTaskId?: string;
  scope?: "mine" | "all";
  page?: number;
  pageSize?: number;
}

/** run 列表（历史记录）；翻页/筛选经 queryKey 变化自动重取。 */
export function useAgentRuns(params: AgentRunListParams = {}) {
  return useQuery({
    queryKey: agentRunQueryKeys.list(params),
    queryFn: () => agentRunsApi.list(params),
  });
}

/** run 详情快照。 */
export function useAgentRun(id: string) {
  return useQuery({
    queryKey: agentRunQueryKeys.detail(id),
    queryFn: () => agentRunsApi.get(id).then((res) => res.data),
    enabled: !!id,
    retry: false,
  });
}

/** 应答 awaiting_input（POST input）；成功后按事件流 / 快照收敛状态。 */
export function useSubmitAgentRunInput(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentRunInputPayload) => agentRunsApi.submitInput(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentRunQueryKeys.detail(id) });
    },
  });
}

/** 请求取消；成功后重拉快照以取得 cancelledAt（「正在停止」中间态的依据）。 */
export function useCancelAgentRun(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => agentRunsApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentRunQueryKeys.detail(id) });
    },
  });
}

/** 创建即跑（发起表单提交用）。 */
export function useCreateAgentRun() {
  return useMutation({
    mutationFn: (payload: AgentRunCreatePayload) =>
      agentRunsApi.create(payload).then((res) => res.data),
  });
}
